import { Dispatch, SetStateAction, useCallback, useState } from "react"

import { FinalSuccessDialogMeetingService } from "../../components/finalSuccessDialog/FinalSuccessDialogMeetingService"
import { ReceiptConfirmOrderDialog } from "../../../dialogs/receiptConfirmOrderDialog/ReceiptConfirmOrderDialog"
import {
    CreateUpdateMeetingServiceOrder,
    OrganizerSelectedValue,
    SelectedMainUserState,
    SelectedPriceListItem,
    CreateOrderDialogMode,
    AvailableModules,
} from "../../../createOrder/createOrderTypes"
import { createMeetingServiceOrder, updateMeetingServiceOrder } from "../../createOrderApi"
import { OrderContextState, OrderFormStatus } from "../../mainForm/helpers"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { extractAccountingDimensions } from "../../components/form/helpers"
import { DynamicConfigKey, MeetingServiceTabValues, PaymentMethodOptionValue } from "../../components/form/formTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { CreateOrderType } from "../../helpers"
import { DIALOG_TO_CREDIT_ACTION, mapToDeliveries } from "./helpers"
import { makeRecurrencePayload } from "../drawer/meetingDetails/helpers"
import { RepeatOrderFormValues } from "../drawer/meetingDetails/RepeatOrderDialog"
import { PaymentMethodType } from "../../../../../modules/payments/paymentTypes"
import { ServiceCenterHomeQuery } from "../../../../../modules/serviceCenter/report/home/serviceCenterHomeApi"
import { ServiceCenterUpcomingMeetingsQuery } from "../../../../../modules/serviceCenter/report/upcomingBookings/upcomingMeetingsApi"
import { Organizer, OrganizerType } from "../../../../types/OrganizerTypes"
import { isSharedCalendarOrder, toCalendarOrganizerPayload } from "../../../../../modules/order/pages/helpers"
import { createCreditNote, createSupplementaryOrder } from "../../../dialogs/creditNoteDialog/creditNoteApi"
import { defaultOrderContextState } from "../../defaults"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { useCreateOrderFormConfig } from "../../mainForm/CreateOrderFormContext"
import { stripAttendeeSource } from "../../outlookAttendees"

type ReceiptConfirmOrderDialogWrapProps = Readonly<{
    open: boolean
    onClose: () => void
    orderDetails: MeetingServiceTabValues
    mainUser: SelectedMainUserState
    handleCloseMainDialog: () => void
    handleOpenResourceBooking: (type: CreateOrderType) => void
    handleAddNewMeetingService: () => void
    orderContext: OrderContextState
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    availableModules: AvailableModules
}>

export function ReceiptConfirmOrderDialogWrap({
    open,
    onClose,
    orderDetails,
    mainUser,
    orderContext,
    setOrderContext,
    handleCloseMainDialog,
    handleOpenResourceBooking,
    handleAddNewMeetingService,
    orderFormStatusRef,
    availableModules,
}: ReceiptConfirmOrderDialogWrapProps) {
    const { orderId, mode } = orderContext
    const { isFullInitOrder, meetingServiceId } = orderFormStatusRef.current

    const dateTimeFactory = useDateTime()
    const { dialogType, flowConfig, orderRecurrencePattern, calendarOrganizer, orderPeople } =
        useCreateOrderFormConfig()
    const [finalSuccessDialog, setFinalSuccessDialog] = useState<{
        order: CreateUpdateMeetingServiceOrder
        orderContextId?: number
        savedOrderId?: number
        savedAsUpdate?: boolean
        seriesOrderCount?: number
    } | null>(null)
    const { isRegularOrder, isSpecialOrder, isRefundOrder } = useCreateOrderDialogType()
    const creditAction = DIALOG_TO_CREDIT_ACTION[dialogType]

    const { mutateAsync: callCreateMeetingServiceOrder, isLoading: isCreating } = useApiMutation(
        createMeetingServiceOrder,
        {
            async onSuccess(params, data, context) {
                const id = data?.order?.id
                orderFormStatusRef.current.meetingServiceId = id
                await context.queryClient.invalidateQueries({
                    queryKey: [ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS],
                    refetchType: "none",
                })
                await context.queryClient.invalidateQueries({
                    queryKey: [ServiceCenterUpcomingMeetingsQuery.UPCOMING_MEETINGS],
                    refetchType: "none",
                })
                setFinalSuccessDialog({
                    order: params.content,
                    orderContextId: id ?? orderId,
                    savedOrderId: id,
                    seriesOrderCount: data?.order?.meetingDetails?.recurrence?.recurrenceOrders?.length,
                })
            },
        },
    )

    const { mutateAsync: callUpdateMeetingServiceOrder, isLoading: isUpdating } = useApiMutation(
        updateMeetingServiceOrder,
        {
            async onSuccess(params, _data, context) {
                await context.queryClient.invalidateQueries({
                    queryKey: [ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS],
                    refetchType: "none",
                })
                await context.queryClient.invalidateQueries({
                    queryKey: [ServiceCenterUpcomingMeetingsQuery.UPCOMING_MEETINGS],
                    refetchType: "none",
                })
                setFinalSuccessDialog({
                    order: params.content,
                    savedOrderId: params.orderId,
                    savedAsUpdate: true,
                })
            },
        },
    )

    const { mutateAsync: callCreateCreditNote, isLoading: isCreatingCreditNote } = useApiMutation(createCreditNote, {
        async onSuccess(params, data) {
            const id = data?.order?.id
            orderFormStatusRef.current.meetingServiceId = id
            setFinalSuccessDialog({ order: params.order, orderContextId: id ?? orderId, savedOrderId: id })
        },
    })

    const { mutateAsync: callCreateSupplementaryOrder, isLoading: isCreatingSupplementaryOrder } = useApiMutation(
        createSupplementaryOrder,
        {
            async onSuccess(params, data) {
                const id = data?.order?.id
                orderFormStatusRef.current.meetingServiceId = id
                setFinalSuccessDialog({ order: params.order, orderContextId: id ?? orderId, savedOrderId: id })
            },
        },
    )

    const isSubmitting = isCreating || isUpdating || isCreatingCreditNote || isCreatingSupplementaryOrder
    const hasCatering = isFullInitOrder || !!meetingServiceId

    const handleConfirm = async () => {
        if (isSubmitting) return

        const isOutlookFlow = !!flowConfig
        const sharedCalendarOrganizer = isSharedCalendarOrder(
            flowConfig?.isSharedCalendar,
            calendarOrganizer,
            orderPeople,
        )
            ? calendarOrganizer
            : undefined

        // Only a *shared* calendar needs an organizer of its own: there ORGANIZER belongs to the
        // calendar, so the acting user moves to CREATOR — where the id-based consumers already fall
        // back to once they recognise ORGANIZER as the calendar. On the user's own calendar the two
        // are the same mailbox, so the address just rides on their ORGANIZER entry, displacing nothing.
        // The person stays first for the consumers that read `organizers[0]` positionally.
        // KNOWN SERVER-SIDE GAP (measured 2026-09-09): on a shared calendar this layout is correct for
        // `/orders/bookings` but `/orders/meetingService` cannot store it — see the four attempts in
        // the bug report. Every client-side shape either fails or silently rewrites RESPONSIBLE, so we
        // keep the correct payload and let the API's error surface rather than corrupting the order.
        const organizers: Organizer[] = sharedCalendarOrganizer
            ? [
                  { id: mainUser.userId, type: OrganizerType.CREATOR },
                  { ...toCalendarOrganizerPayload(sharedCalendarOrganizer), type: OrganizerType.ORGANIZER },
              ]
            : [
                  {
                      id: mainUser.onBehalfOf?.userId ?? mainUser.userId,
                      type: OrganizerType.ORGANIZER,
                      // Copied only inside the add-in flow, where the address was just resolved for
                      // this very meeting. On a saved order re-opened from Manager the entry we are
                      // rebuilding may belong to somebody else, and guessing would misattribute it.
                      ...(isOutlookFlow && calendarOrganizer?.email ? { email: calendarOrganizer.email } : {}),
                  },
              ]

        const organizersValue: OrganizerSelectedValue = orderDetails[DynamicConfigKey.MEETING_RESPONSIBLE]

        // The id must be sent even when the responsible is the main user: a RESPONSIBLE without an
        // id is persisted with id 0, which permanently turns the host into an unlinked text label
        // (no user reference, no email on the receipt) and re-saving the order does not repair it.
        //
        // On a shared calendar the calendar already takes the one id-less organizer the API
        // tolerates — updating an order that carries two of them fails with a server-side
        // NullPointerException — so a name-only responsible cannot go with it. Fall back to the
        // acting user by id rather than dropping RESPONSIBLE: without one `getOrderPerson` resolves
        // the meeting host to the calendar, and every consumer of it renders a mailbox address.
        // On the user's own calendar that slot is free, so a hand-typed host survives as before.
        if (organizersValue?.id) {
            organizers.push({
                id: organizersValue.id,
                type: OrganizerType.RESPONSIBLE,
            })
        } else if (sharedCalendarOrganizer) {
            organizers.push({
                id: mainUser.userId,
                type: OrganizerType.RESPONSIBLE,
            })
        } else if (organizersValue?.name && organizersValue.name.trim()) {
            organizers.push({
                type: OrganizerType.RESPONSIBLE,
                name: organizersValue.name.trim(),
            })
        }

        const deliveries = mapToDeliveries(orderDetails)

        const accounting = {
            dimensions: extractAccountingDimensions(orderDetails),
        }

        const priceList: SelectedPriceListItem = orderDetails["priceList"].item!
        const recurrency: RepeatOrderFormValues = orderDetails[DynamicConfigKey.RECURRENCY]
        // Outlook-linked orders lock the recurrence rule to Outlook: use the pattern from the
        // Outlook session verbatim instead of recomputing it from the (locked) Manager form.
        const outlookRecurrencePattern = flowConfig?.order?.meetingDetails?.recurrence?.pattern
        const paymentValue: PaymentMethodOptionValue =
            orderDetails[DynamicConfigKey.PAYMENT_METHOD]?.value ?? PaymentMethodType.INVOICE
        const workgroupId = orderDetails.workgroupId
        const workgroup = isOutlookFlow ? {} : { workgroup: workgroupId ? { id: Number(workgroupId) } : null }
        const onBehalfOf = flowConfig?.onBehalfOf ?? mainUser.onBehalfOf

        const transformData = {
            merchant: { id: priceList.merchantId },
            webshop: {
                id: priceList.shopId,
            },
            priceList: {
                id: priceList.priceListId,
            },
            deliveries,
            meetingDetails: {
                meetingName: orderDetails[DynamicConfigKey.MEETING_NAME],
                ...(outlookRecurrencePattern
                    ? { recurrence: { pattern: outlookRecurrencePattern } }
                    : orderRecurrencePattern
                      ? // Read-only here and richer than the form can represent — omit it so the
                        // backend keeps the stored pattern. Keyed on the pattern, not the Outlook
                        // link, or an ordinary Manager recurrence would be discarded.
                        {}
                      : recurrency
                        ? { recurrence: makeRecurrencePayload(recurrency) }
                        : {}),
                ...(orderDetails["event"]?.start
                    ? { startDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(orderDetails["event"]?.start)) }
                    : {}),

                ...(orderDetails["event"]?.end
                    ? { endDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(orderDetails["event"]?.end)) }
                    : {}),

                attendees: stripAttendeeSource(orderDetails[DynamicConfigKey.REGISTER_PARTICIPANTS] ?? []),
                organizers,
                ...(flowConfig?.calendarAppointmentId && { calendarAppointmentId: flowConfig.calendarAppointmentId }),
            },
            payment:
                paymentValue === PaymentMethodType.INVOICE
                    ? {
                          buyerCustomerParty: {
                              id: orderDetails[DynamicConfigKey.BUYER_PARTIES].id,
                          },

                          method: PaymentMethodType.INVOICE,
                          accounting,
                      }
                    : {
                          method: PaymentMethodType.CASH,
                      },
            ...workgroup,
            orderNote: orderDetails[DynamicConfigKey.MERCHANT_ORDER_NOTE],
            orderType: "MEETING_SERVICE",
            ...(onBehalfOf ? { onBehalfOf: { userId: onBehalfOf.userId } } : {}),
        }

        if (isRegularOrder) {
            if (orderId && hasCatering && mode !== CreateOrderDialogMode.DUPLICATE) {
                await callUpdateMeetingServiceOrder({ userId: mainUser.userId, orderId, content: transformData })
            } else {
                await callCreateMeetingServiceOrder({
                    userId: mainUser.userId,
                    content: {
                        ...transformData,
                        ...(orderId && mode !== CreateOrderDialogMode.DUPLICATE ? { order: { id: orderId } } : {}),
                    },
                })
            }
        }

        if (isSpecialOrder) {
            if (!orderId || !creditAction) return

            const specialContent = {
                ...transformData,
                payment: {
                    ...transformData.payment,
                    type: creditAction,
                },
            }

            if (isRefundOrder) {
                await callCreateCreditNote({ orderId, order: specialContent })
            } else {
                await callCreateSupplementaryOrder({ orderId, order: specialContent })
            }
        }
    }

    const handleFinalSuccessClose = useCallback(() => {
        finalSuccessDialog?.orderContextId &&
            setOrderContext((prev) => {
                return { ...prev, orderId: finalSuccessDialog.orderContextId }
            })

        setFinalSuccessDialog(null)
    }, [finalSuccessDialog?.orderContextId])

    const handleAddNewMeetingServiceClick = useCallback(() => {
        setOrderContext(defaultOrderContextState)
        handleAddNewMeetingService()
    }, [setOrderContext, handleAddNewMeetingService])

    return (
        <>
            <ReceiptConfirmOrderDialog
                open={open}
                onClose={onClose}
                handleConfirm={handleConfirm}
                dialogType={dialogType}
                orderDetails={orderDetails}
                mainUser={mainUser}
                orderId={mode !== CreateOrderDialogMode.DUPLICATE && hasCatering ? orderId : undefined}
                isSubmitting={isSubmitting}
            />
            {finalSuccessDialog && (
                <FinalSuccessDialogMeetingService
                    orderDetails={finalSuccessDialog.order}
                    onClose={handleFinalSuccessClose}
                    handleAddNewMeetingService={handleAddNewMeetingServiceClick}
                    handleCloseMainDialog={handleCloseMainDialog}
                    handleOpenResourceBooking={handleOpenResourceBooking}
                    orderFormStatusRef={orderFormStatusRef}
                    availableModules={availableModules}
                    orderId={finalSuccessDialog.savedOrderId}
                    savedAsUpdate={finalSuccessDialog.savedAsUpdate}
                    seriesOrderCount={finalSuccessDialog.seriesOrderCount}
                />
            )}
        </>
    )
}
